import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import { ArrowLeft, Send, FileText, Upload } from "lucide-react";

const emptyForm = {
  company_name: "", kvk_number: "", btw_number: "", iban: "",
  contact_person: "", contact_email: "", contact_phone: "",
  street: "", house_number: "", postal_code: "", city: "", country: "Netherlands",
  website_url: "", instagram_url: "", facebook_url: "", tiktok_url: "",
  bio_nl: "", bio_en: "",
  terms_accepted: false,
};

const SellApply = () => {
  const { user } = useAuth();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate("/login?next=/sell/apply");
      return;
    }
    apiClient.get("/sellers/me")
      .then((r) => {
        setExisting(r.data);
        if (r.data.status === "approved") {
          navigate("/seller");
          return;
        }
        if (r.data.status === "pending" || r.data.status === "rejected") {
          // Pre-fill
          setForm({ ...emptyForm, ...r.data, terms_accepted: !!r.data.terms_accepted });
        }
      })
      .catch(() => {});
  }, [user, navigate]);

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.terms_accepted) {
      toast.error(lang === "nl" ? "Accepteer de voorwaarden" : "Please accept the terms");
      return;
    }
    setBusy(true);
    try {
      await apiClient.post("/sellers/apply", form);
      toast.success(lang === "nl" ? "Aanvraag ingediend! We laten je het weten zodra goedgekeurd." : "Application submitted! We'll let you know once approved.");
      navigate("/seller/status");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (existing?.status === "pending") {
    return (
      <div className="max-w-[800px] mx-auto px-6 py-16 text-center" data-testid="seller-application-pending">
        <h1 className="font-display text-4xl tracking-tighter">{lang === "nl" ? "Aanvraag in behandeling" : "Application under review"}</h1>
        <p className="text-ink-muted mt-3">{lang === "nl" ? "Wij beoordelen je gegevens binnen 1-2 werkdagen." : "We're reviewing your details within 1-2 business days."}</p>
        <Link to="/seller/status" className="btn-primary mt-6">{lang === "nl" ? "Status bekijken" : "View status"}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1000px] mx-auto px-6 lg:px-12 py-12" data-testid="seller-apply-page">
      <Link to="/sell" className="font-label text-ink-muted hover:text-ink flex items-center gap-1.5 mb-3"><ArrowLeft className="w-3.5 h-3.5" /> {lang === "nl" ? "Terug" : "Back"}</Link>
      <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light">{lang === "nl" ? "Verkoper aanvraag" : "Seller application"}</h1>
      <p className="text-ink-muted mt-2 max-w-2xl">{lang === "nl" ? "Vul je bedrijfsgegevens in. Alle velden met * zijn verplicht. Wij beoordelen je aanvraag binnen 1-2 werkdagen." : "Fill in your company details. All fields marked * are required. We'll review your application within 1-2 business days."}</p>

      <form onSubmit={submit} className="mt-10 space-y-10">
        <Section title={lang === "nl" ? "Bedrijfsgegevens" : "Company details"}>
          <F label="Company name *" value={form.company_name} onChange={(v) => upd("company_name", v)} required testid="apply-company" />
          <F label="KvK number *" value={form.kvk_number} onChange={(v) => upd("kvk_number", v)} placeholder="12345678" required testid="apply-kvk" />
          <F label="BTW/VAT number *" value={form.btw_number} onChange={(v) => upd("btw_number", v)} placeholder="NL123456789B01" required testid="apply-btw" />
          <F label="IBAN *" value={form.iban} onChange={(v) => upd("iban", v)} placeholder="NL00 BANK 0000 0000 00" required testid="apply-iban" />
        </Section>

        <Section title={lang === "nl" ? "Bedrijfsadres" : "Business address"}>
          <F label={lang === "nl" ? "Straat *" : "Street *"} value={form.street} onChange={(v) => upd("street", v)} required />
          <F label={lang === "nl" ? "Huisnr. *" : "House no. *"} value={form.house_number} onChange={(v) => upd("house_number", v)} required />
          <F label={lang === "nl" ? "Postcode *" : "Postal code *"} value={form.postal_code} onChange={(v) => upd("postal_code", v)} required />
          <F label={lang === "nl" ? "Stad *" : "City *"} value={form.city} onChange={(v) => upd("city", v)} required />
          <F label={lang === "nl" ? "Land *" : "Country *"} value={form.country} onChange={(v) => upd("country", v)} required />
        </Section>

        <Section title={lang === "nl" ? "Contactpersoon" : "Contact person"}>
          <F label={lang === "nl" ? "Naam *" : "Full name *"} value={form.contact_person} onChange={(v) => upd("contact_person", v)} required testid="apply-contact-name" />
          <F type="email" label="Email *" value={form.contact_email} onChange={(v) => upd("contact_email", v)} required testid="apply-contact-email" />
          <F label={lang === "nl" ? "Telefoon *" : "Phone *"} value={form.contact_phone} onChange={(v) => upd("contact_phone", v)} required testid="apply-contact-phone" />
        </Section>

        <Section title={lang === "nl" ? "Website & social (optioneel)" : "Website & social (optional)"}>
          <F label="Website" value={form.website_url} onChange={(v) => upd("website_url", v)} placeholder="https://..." />
          <F label="Instagram" value={form.instagram_url} onChange={(v) => upd("instagram_url", v)} placeholder="https://instagram.com/..." />
          <F label="Facebook" value={form.facebook_url} onChange={(v) => upd("facebook_url", v)} placeholder="https://facebook.com/..." />
          <F label="TikTok" value={form.tiktok_url} onChange={(v) => upd("tiktok_url", v)} placeholder="https://tiktok.com/@..." />
        </Section>

        <Section title={lang === "nl" ? "Over je merk (verschijnt op je verkoperspagina)" : "About your brand (shown on your seller page)"}>
          <Textarea label="Bio NL" value={form.bio_nl} onChange={(v) => upd("bio_nl", v)} />
          <Textarea label="Bio EN" value={form.bio_en} onChange={(v) => upd("bio_en", v)} />
        </Section>

        <div className="border border-black/10 bg-white p-6">
          <div className="font-label text-ink-muted mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> {lang === "nl" ? "Voorwaarden" : "Terms"}</div>
          <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="apply-accept-terms">
            <input type="checkbox" checked={form.terms_accepted} onChange={(e) => upd("terms_accepted", e.target.checked)} className="mt-1" />
            <span>
              {lang === "nl" ? (
                <>Ik accepteer de <Link to="/legal/seller-terms" className="underline" target="_blank">Algemene Voorwaarden voor Verkopers</Link> en het <Link to="/legal/marketplace-policy" className="underline" target="_blank">Marketplace Beleid</Link>. Ik bevestig dat ik <strong>eigen voorraad houd</strong> en <strong>geen dropshipping</strong> doe.</>
              ) : (
                <>I accept the <Link to="/legal/seller-terms" className="underline" target="_blank">Seller Terms & Conditions</Link> and the <Link to="/legal/marketplace-policy" className="underline" target="_blank">Marketplace Policy</Link>. I confirm that I <strong>hold my own stock</strong> and do <strong>not dropship</strong>.</>
              )}
            </span>
          </label>
        </div>

        <div className="flex items-center justify-between">
          {existing?.status === "rejected" && (
            <div className="text-sm text-terracotta">
              {lang === "nl" ? "Vorige aanvraag afgewezen" : "Previous application rejected"}: <em>{existing.rejection_reason || "—"}</em>
            </div>
          )}
          <button type="submit" disabled={busy} className="btn-primary ml-auto" data-testid="apply-submit">
            <Send className="w-4 h-4" /> {busy ? "…" : (lang === "nl" ? "Aanvraag indienen" : "Submit application")}
          </button>
        </div>
      </form>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="border border-black/10 bg-white p-6">
    <div className="font-label text-ink-muted mb-4">{title}</div>
    <div className="grid md:grid-cols-2 gap-4">{children}</div>
  </div>
);
const F = ({ label, value, onChange, required, placeholder, type = "text", testid }) => (
  <label className="block">
    <div className="text-xs font-label text-ink-muted mb-1">{label}</div>
    <input data-testid={testid} type={type} value={value ?? ""} required={required} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="border border-black/15 px-3 py-2 text-sm w-full bg-white" />
  </label>
);
const Textarea = ({ label, value, onChange }) => (
  <label className="block">
    <div className="text-xs font-label text-ink-muted mb-1">{label}</div>
    <textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="border border-black/15 px-3 py-2 text-sm w-full bg-white" />
  </label>
);

export default SellApply;
